import { useCallback, useEffect, useState } from 'react';
import { incidentsApi } from '../lib/api';
import { useAuth } from '../context/AuthContext';

/**
 * Evento disparado (window) quando o piloto envia com sucesso uma defesa.
 * Os consoles ouvem esse evento para remover imediatamente o alerta/banner
 * de pendência sem esperar o próximo refetch.
 */
export const DEFENSE_SUBMITTED_EVENT = 'defense-submitted';

export interface PendingDefenseIncident {
  incident_id: string;
  event_name?: string;
  league_name?: string;
  description?: string;
  incident_type?: string;
  created_at?: string;
  /** Prazo de defesa calculado pelo backend (created_at + defense_hours) */
  deadline_at?: string | null;
}

/**
 * Incidentes abertos em que o usuário atual é o piloto reportado (acusado)
 * e ainda NÃO submeteu defesa — ou seja, pendências de acusação que exigem
 * a atenção do piloto.
 */
export function usePendingDefenses() {
  const { emssUser } = useAuth();
  const [pendingDefenses, setPendingDefenses] = useState<PendingDefenseIncident[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  const refetch = useCallback(async () => {
    if (!emssUser?.id) {
      setPendingDefenses([]);
      return;
    }
    try {
      setIsLoading(true);
      const incidents = await incidentsApi.list({ status: 'open' });
      const openDefenses = ((incidents || []) as any[]).filter(
        (inc) => inc.reported_pilot_id === emssUser.id && !inc.defense_text
      );
      setPendingDefenses(openDefenses);
    } catch (err) {
      console.error('Failed to fetch pending defenses:', err);
    } finally {
      setIsLoading(false);
    }
  }, [emssUser?.id]);

  useEffect(() => {
    refetch();

    const onDefenseSubmitted = () => refetch();
    window.addEventListener(DEFENSE_SUBMITTED_EVENT, onDefenseSubmitted);
    window.addEventListener('focus', onDefenseSubmitted);
    return () => {
      window.removeEventListener(DEFENSE_SUBMITTED_EVENT, onDefenseSubmitted);
      window.removeEventListener('focus', onDefenseSubmitted);
    };
  }, [refetch]);

  return { pendingDefenses, isLoading, refetch };
}
